import { NavigateFunction } from 'react-router'
import { Dispatch } from '@reduxjs/toolkit'

import {
    delPlanningPreference,
    getPreference,
    getPreferencesForMe,
    postPlanningPreference,
} from '@/helpers/api/preferencesApi'
import { createLatestRunGuard } from '@/helpers/createLatestRunGuard'
import { getEndOfWorkDay, getStartOfWorkDay } from '@/helpers/date'
import { trans } from '@/helpers/trans'
import {
    PREFERENCE_CREATE_PLANNING_PREFERENCE_FAILURE,
    PREFERENCE_CREATE_PLANNING_PREFERENCE_REQUEST,
    PREFERENCE_CREATE_PLANNING_PREFERENCE_SUCCESS,
    PREFERENCE_DEL_PLANNING_PREFERENCE_FAILURE,
    PREFERENCE_DEL_PLANNING_PREFERENCE_REQUEST,
    PREFERENCE_DEL_PLANNING_PREFERENCE_SUCCESS,
    PREFERENCE_GET_PLANNING_PREFERENCE_FAILURE,
    PREFERENCE_GET_PLANNING_PREFERENCE_REQUEST,
    PREFERENCE_GET_PLANNING_PREFERENCE_SUCCESS,
    PREFERENCE_GET_PLANNING_PREFERENCES_FAILURE,
    PREFERENCE_GET_PLANNING_PREFERENCES_REQUEST,
    PREFERENCE_GET_PLANNING_PREFERENCES_SUCCESS,
    SET_LOADING,
} from '@/redux/actionTypes'
import { Reducers } from '@/redux/reducers'
import { setToast } from '@/redux/toasts/toastsReducer'
import { ToastType } from '@/types'

const startPreferenceDaysRun = createLatestRunGuard()

export const getPreferenceDays = () => async (dispatch: Dispatch, getState: () => Reducers): Promise<void> => {
    const isLatestRun = startPreferenceDaysRun()

    dispatch({ type: SET_LOADING, loading: true })
    dispatch({ type: PREFERENCE_GET_PLANNING_PREFERENCES_REQUEST })

    try {
        const { selectedDays } = getState().calendarReducer
        const startDate = selectedDays[0]
        const endDate = selectedDays[selectedDays.length - 1]
        const response = await getPreferencesForMe(
            getStartOfWorkDay(startDate),
            getEndOfWorkDay(endDate),
        )

        if (! isLatestRun()) return

        dispatch({
            type: PREFERENCE_GET_PLANNING_PREFERENCES_SUCCESS,
            planningPreferences: response.data,
        })
    } catch (error) {
        if (! isLatestRun()) return

        console.error('[fetchPreferenceDays]', error)
        dispatch({ type: PREFERENCE_GET_PLANNING_PREFERENCES_FAILURE })
    }

    dispatch({ type: SET_LOADING, loading: false })
}

export const getPlanningPreference = (id: string) => async (dispatch: Dispatch): Promise<void> => {
    dispatch({ type: SET_LOADING, loading: true })
    dispatch({ type: PREFERENCE_GET_PLANNING_PREFERENCE_REQUEST })

    try {
        const response = await getPreference(id)

        dispatch({
            type: PREFERENCE_GET_PLANNING_PREFERENCE_SUCCESS,
            planningPreference: response.data,
        })
    } catch (error) {
        console.error('[fetchPreferenceDay]', error)
        dispatch({ type: PREFERENCE_GET_PLANNING_PREFERENCE_FAILURE })
    }

    dispatch({ type: SET_LOADING, loading: false })
}

export const createPlanningPreference = (
    startDate: string,
    endDate: string,
    reason: string,
    navigate: NavigateFunction,
) => async (dispatch: Dispatch, getState: () => Reducers): Promise<void> => {
    dispatch({ type: PREFERENCE_CREATE_PLANNING_PREFERENCE_REQUEST })

    try {
        const { currentUser } = getState().userReducer
        await postPlanningPreference(startDate, endDate, reason, currentUser?.id)
        dispatch({ type: PREFERENCE_CREATE_PLANNING_PREFERENCE_SUCCESS })
        navigate(`/${trans('nav.planningPreferences.link')}`)
        const toast = trans('pages.planningPreferences.createPlanningPreferenceSuccess')
        dispatch(setToast({ type: ToastType.pass, text: toast }))
    } catch (error) {
        console.error('[createPlanningPreference]', error)
        dispatch({ type: PREFERENCE_CREATE_PLANNING_PREFERENCE_FAILURE })
    }
}

export const deletePlanningPreference = (id: string, navigate: NavigateFunction) => async (dispatch: Dispatch): Promise<void> => {
    dispatch({ type: PREFERENCE_DEL_PLANNING_PREFERENCE_REQUEST })

    try {
        await delPlanningPreference(id)
        dispatch({ type: PREFERENCE_DEL_PLANNING_PREFERENCE_SUCCESS })
        navigate(`/${trans('nav.planningPreferences.link')}`)
        const toast = trans('pages.planningPreferences.deletePlanningPreferenceSuccess')
        dispatch(setToast({ type: ToastType.pass, text: toast }))
    } catch (error) {
        console.error('[deletePlanningPreference]', error)
        dispatch({ type: PREFERENCE_DEL_PLANNING_PREFERENCE_FAILURE })
    }
}
